/**
 * Turns resolved `AudioSegment`s into a WebAudio graph. Used identically for realtime preview
 * (AudioContext) and export (OfflineAudioContext), so both mix exactly the same way.
 */
import { cropEnvelope, type AudioSegment } from '@ve/core';

export interface ScheduledAudio {
  /** Stops and disconnects all nodes. Safe to call multiple times. */
  stop(): void;
  /** Media ids whose buffers were unavailable, so their segments were skipped. */
  missing: Set<string>;
}

export interface SchedulePlan {
  /** Timeline time (seconds) that corresponds to `contextTime`. */
  timelineTime: number;
  /** Context time (seconds) at which `timelineTime` is played. */
  contextTime: number;
  /** Optional timeline end (exclusive). */
  endTime?: number;
}

export interface SegmentWindow {
  /** Timeline window actually played. */
  from: number;
  to: number;
  /** Context time to start. */
  when: number;
  /** Offset into the buffer (source seconds). */
  offset: number;
  /** Buffer seconds to play. */
  bufferDuration: number;
}

/** Pure: computes what part of a segment plays for a schedule plan, or null if none. */
export function segmentWindow(seg: AudioSegment, plan: SchedulePlan): SegmentWindow | null {
  const segEnd = seg.start + seg.duration;
  const from = Math.max(seg.start, plan.timelineTime);
  const to = Math.min(segEnd, plan.endTime ?? Infinity);
  if (to - from <= 1e-4) return null;
  const offset = seg.sourceStart + (from - seg.start) * seg.speed;
  return {
    from,
    to,
    when: plan.contextTime + (from - plan.timelineTime),
    offset: Math.max(0, offset),
    bufferDuration: (to - from) * seg.speed,
  };
}

export function scheduleAudio(
  ctx: BaseAudioContext,
  destination: AudioNode,
  segments: readonly AudioSegment[],
  getBuffer: (mediaId: string) => AudioBuffer | undefined,
  plan: SchedulePlan,
): ScheduledAudio {
  const nodes: Array<{ src: AudioBufferSourceNode; gain: GainNode }> = [];
  const missing = new Set<string>();
  for (const seg of segments) {
    const win = segmentWindow(seg, plan);
    if (!win) continue;
    const buffer = getBuffer(seg.mediaId);
    if (!buffer) {
      missing.add(seg.mediaId);
      continue;
    }
    if (win.offset >= buffer.duration) continue;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = seg.speed;
    const gain = ctx.createGain();
    const env = cropEnvelope(seg.gain, win.from, win.to);
    const param = gain.gain;
    const when = Math.max(win.when, ctx.currentTime);
    param.setValueAtTime(env.values[0] ?? 1, when);
    for (let i = 1; i < env.times.length; i++) {
      const t = win.when + (env.times[i]! - win.from);
      if (t <= when) {
        param.setValueAtTime(env.values[i]!, when);
      } else {
        param.linearRampToValueAtTime(env.values[i]!, t);
      }
    }
    src.connect(gain).connect(destination);
    // If we're already past `win.when` (late scheduling), skip ahead in the buffer accordingly.
    const late = when - win.when;
    src.start(when, win.offset + late * seg.speed, Math.max(0, win.bufferDuration - late * seg.speed));
    nodes.push({ src, gain });
  }
  let stopped = false;
  return {
    missing,
    stop() {
      if (stopped) return;
      stopped = true;
      for (const { src, gain } of nodes) {
        try {
          src.stop();
        } catch {
          // already stopped
        }
        src.disconnect();
        gain.disconnect();
      }
    },
  };
}
